import type { Directive } from 'vue'
import { navigateTo, getObjectFromUrlParams } from '#imports'

/**
 * Checks for special hash fragments in a URL and converts the first one found
 * into a query parameter. It merges this with existing query parameters from the
 * current route, while explicitly filtering out promotion/tracking parameters.
 * @param href The original link's href.
 * @returns The modified href or the original href if no special fragment is found.
 */
const handleSpecialHash = (href: string) => {
    const specialFragments = [
        '#terms',
        '#general-terms',
        '#privacy-policy',
        '#excluded-games',
        '#bingo-games'
    ]

    const foundFragment = specialFragments.find(fragment => href.includes(fragment))

    if (!foundFragment) return href

    const currentQuery = getObjectFromUrlParams(window.location.href)
    const fragmentParam = foundFragment.substring(1)

    return {
        query: { [fragmentParam]: 'true', ...currentQuery }
    }
}

/**
 * Vue directive that converts internal anchor tags within an element to behave like NuxtLinks.
 * It prevents default link behavior for internal routing,
 * also handling specific hash fragment to query parameter conversion.
 */
export const vInternalLinks: Directive = {
    mounted(el: HTMLElement) {
        const handleClick = async (event: Event) => {
            const anchor = (event.target as HTMLElement).closest('a') as HTMLAnchorElement

            if (!anchor) return

            const href = anchor.getAttribute('href')

            if (!href) return

            const isExternal = href.startsWith('http')
                || href.startsWith('mailto:')
                || href.startsWith('tel:')

            if (isExternal) return

            const navigationObject = handleSpecialHash(href)

            event.preventDefault()
            await navigateTo(navigationObject)
        }

        // Store the handler for cleanup
        (el as any)._linksHandler = handleClick

        el.addEventListener('click', handleClick)
    },

    unmounted(el: HTMLElement) {
        const handler = (el as any)._linksHandler

        if (handler) {
            el.removeEventListener('click', handler)

            delete (el as any)._linksHandler
        }
    }
}
